/**
 *  @class
 *  @function StickyScroller
 */
if (!customElements.get('sticky-scroller')) {
  class StickyScroller extends HTMLElement {

    constructor() {
      super();
      this.headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
      this.parent = this.closest('.sticky-scroller--parent');
      this.newScrollPosition = 0;
      this.oldScrollPositon = 0;
      this.ticking = false;

      // ✅ lock to prevent forced scroll during layout changes
      this.locked = false;
      this.lockTimeout = null;
    }

    connectedCallback() {

      this.element = this.querySelector('.sticky-scroller--element');
      this.siblings = this.parent?.querySelectorAll('.sticky-scroller--compare');
      this.collapsibles = this.parent.querySelectorAll('.accordion');

      window.addEventListener('resize', this.onResize.bind(this));
      window.addEventListener('scroll', this.onScroll.bind(this), {
        passive: true
      });

      window.dispatchEvent(new Event('resize'));

      if (this.collapsibles.length) {
        this.collapsibles.forEach((collapsible) => {

          /* -------------------------------
             LOCK ON <details> TOGGLE
          --------------------------------*/
          const details = collapsible.querySelector('details');
          if (details) {
            details.addEventListener('toggle', () => {
              this.lockScrollTemporarily();
            });
          }

          /* -------------------------------
             LOCK ON <summary> CLICK
          --------------------------------*/
          const summary = collapsible.querySelector('summary');
          if (summary) {
            summary.addEventListener('click', () => {
              this.lockScrollTemporarily();
            });
          }

        });
      }
    }

    lockScrollTemporarily() {
      // Lock immediately
      this.locked = true;

      // Clear any existing timeout
      if (this.lockTimeout) clearTimeout(this.lockTimeout);

      // Keep locked through layout + scroll recalculation
      this.lockTimeout = setTimeout(() => {
        this.locked = false;
      }, 300); // enough time for layout + scroll + resize
    }

    getSiblingsHeight() {
      this.siblingHeight = 0;
      this.siblings.forEach((sibling) => {
        this.siblingHeight += sibling.offsetHeight;
      });
      return this.siblingHeight;
    }

    onResize() {
      // Write first: remove disabled class so offsetHeight reads are accurate
      this.disable(false);
      // Batch all reads together — no interleaved writes
      const siblingsHeight = this.getSiblingsHeight();
      const elementHeight = this.element.offsetHeight;
      // Write last
      if (siblingsHeight) {
        if (siblingsHeight <= elementHeight) {
          this.disable(true);
        } else {
          this.activate();
        }
      }
    }

    onScroll() {
      this.newScrollPosition = window.scrollY;

      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.translate();
          this.ticking = false;
          this.oldScrollPositon = this.newScrollPosition;
        });

        this.ticking = true;
      }
    }

    activate() {
      this.element.style.overflowY = 'hidden';
      this.element.classList.remove('sticky-scroller--disabled');

      this.ticking = false;
      window.dispatchEvent(new Event('scroll'));
    }

    disable(scroll) {
      if (scroll) {
        this.element.style.overflowY = 'auto';
      }
      this.element.classList.add('sticky-scroller--disabled');
    }

    translate() {
      // ✅ HARD STOP: prevent programmatic scroll
      if (this.locked) return;

      const parentRect = this.parentElement.getBoundingClientRect();
      const distance = this.newScrollPosition - this.oldScrollPositon;

      // Do not scroll up before sticky period
      if (parentRect.top > this.headerHeight && distance > 0) {
        return;
      }

      // Do not scroll down after sticky period
      if (parentRect.bottom < window.innerHeight && distance < 0) {
        return;
      }

      // 🚫 This is what caused the bug — now safely controlled
      this.element.scrollTop += distance;
    }
  }

  customElements.define('sticky-scroller', StickyScroller);
}
